import React, { useContext } from 'react';
import { Auth } from 'aws-amplify';
import { OnboardingPageLayout } from '../OnboardingPageLayout';
import { OnboardingWorkspaceSelect } from './OnboardingWorkspaceSelect';
import {
  AuthCompleteInput,
  AuthenticatedPortalDataType,
} from './onboardingLoginTypes';
import {
  FlagsContext,
  PortalConfigContext,
  RouteContext,
} from '../../../context';
import UsersClient from '../../../clients/UsersClient';
import { LoadingWrapper } from '../../Loading';
import { useWorkspaces } from '../../../hooks/useWorkspaces';
import { OnboardingAuthLayout } from './OnboardingAuthLayout';
import { useAppDispatch, useAppSelector } from '../../../hooks/useStore';
import StringUtils from '../../../utils/StringUtils';
import { setProcessingCallback } from '../../../store/auth/slice';
import { OnboardingWorkspaceSelectV2 } from './OnboardingWorkspaceSelect.v2';

export interface queryParams {
  next?: string;
  step?: string;
  email?: string;
  poolId?: string;
  auth?: string; // one time password used when activating user
}

/**
 * When this component is loaded we need to decide whether to show the login page or the workspace select page.
 * The way we decide is by checking the portal-sessions cookie passed in the window
 *
 * We need to show a login page if all sessions are expired or if there are no sessions. In this case a user needs to login.
 * and get a new session. If there is a session that is not expired we can go to the next step
 *
 * Once we have a session that is not expired we can make an api call to /GET /portals/...
 * If there is only one workspace we do not need to show the workspace select component
 * and we can directly go to /portal/portalId. Otherwise we need to show the workspace select component where the user selects
 * a workspace and then we go to /portal/portalId
 *
 * @returns renders the login to portal page and subsequent select workspace component
 */
export const OnboardingLoginPage = () => {
  const [loadingWorkspaces, setLoadingWorkspaces] = React.useState(false);
  const { AuthLayoutsRedesign } = useContext(FlagsContext);
  const [
    authenticatedPortalsData,
    setAuthenticatedPortalsData,
  ] = React.useState<AuthCompleteInput | null>(null);
  const portalConfig = React.useContext(PortalConfigContext);
  const { query }: { query: queryParams } = React.useContext(RouteContext);
  const { getWorkspaces } = useWorkspaces();

  const { googleSignedIn, googleSignInProgress } = useAppSelector(state => ({
    googleSignInError: state.auth.error,
    googleSignInProgress: state.auth.processingCallback,
    googleSignedIn: state.auth.signedIn,
  }));

  const dispatch = useAppDispatch();

  const OnboardingWorkspaceComponent = AuthLayoutsRedesign
    ? OnboardingWorkspaceSelectV2
    : OnboardingWorkspaceSelect;
  /**
   * When login is complete and a workspace is chosen this function will
   * go to the selected workspace. There are two cases:
   * 1. The user is logging in with new credentials and we need to create a new session
   * 2. The user is selecting a portal that they had previously logged into and we can use the existing session
   * @param email user email
   * @param password user password
   * @param portalData authenticated portal data
   */
  const handleWorkspaceSelected = async (
    portalData: AuthenticatedPortalDataType,
  ) => {
    if (!authenticatedPortalsData?.authSession) {
      return;
    }

    const { authSession, authenticatedPortals } = authenticatedPortalsData;
    let existingAuthSession: AuthenticatedPortalDataType | undefined;
    // check if the selected workspaces is already in an existing session
    // AND the email used at login is different than the email in the existing session.
    // If the email is the same then we should continue with creating a new session to
    // replace and expired session information
    Object.entries(authenticatedPortals).forEach(
      ([email, authPortalsByEmail]) =>
        Object.values(authPortalsByEmail).forEach(authPortal => {
          if (authPortal.id === portalData.id && authSession.email !== email) {
            // select workspace found with different email than the one used to login
            existingAuthSession = authPortal;
          }
        }),
    );

    const nextPath = query.next ? `?next=${query.next}` : '';

    if (existingAuthSession) {
      // if there is an existing auth session we can directly navigate there
      // once portal session is set we will use nextPath for redirection
      window.location.href = `/portal/${existingAuthSession.id}${nextPath}`;
      return;
    }

    try {
      const result = await UsersClient.setWorkspace({
        ...authSession,
        portalId: portalData.id,
        hostname: portalData.hostname,
      });
      if (result.data.redirect && authSession.userId) {
        window.location = `${result.data.redirect}${nextPath}`;
      }
    } catch (err) {
      console.error('Error setting workspace', err);
    }
  };
  /**
   * When the onboarding login page is mounted,
   * this function is called to decide whether
   * to show the workspace select component or the login component.
   */
  const loadWorkspaces = async () => {
    try {
      setLoadingWorkspaces(true);
      const {
        portalIdToSession,
        workspaces: workspacesResponse,
      } = await getWorkspaces({ querySession: true });
      if (Object.keys(portalIdToSession).length === 0) {
        setAuthenticatedPortalsData(null);
        return;
      }

      const {
        // workspace_login is a specific session type that is used for the workspace login flow
        workspace_login: workspaceLoginSession,
        ...portalSessions
      } = portalIdToSession;

      // if we have portal id avialable in query
      // redirect user to the portal
      if (query.next && query.email) {
        // checking if the query next url redirects to a portal
        if (query.next.includes('/portal/')) {
          // get portal id from query next
          const nextPathParts = query.next.split('?')[0]; // remove query params to extract portal id;
          const portalId = nextPathParts.replace('/portal/', '');

          const portalData = workspacesResponse[query.email][portalId];
          try {
            await UsersClient.setWorkspace({
              ...workspaceLoginSession,
              portalId: portalData.id,
              hostname: portalData.hostname,
            });

            window.location.replace(query.next);
            return;
          } catch (err) {
            console.error('Error setting workspace', err);
          }
        }
      }

      // when there is only email in the workspace response map
      if (Object.keys(workspacesResponse).length === 1) {
        const email = Object.keys(workspacesResponse)[0];
        // and only one auth portal data object inside that email object
        // we can directly go to the portal
        if (Object.keys(workspacesResponse[email]).length === 1) {
          const portalData = Object.values(workspacesResponse[email])[0];
          const portalId = portalData.id;
          const defaultPortalSessionData = portalSessions[portalId];

          // if there is only one portal workspace found then by default the userId
          // associated with the portal is the same as the one used to login
          const { userId } = workspaceLoginSession;
          if (!userId) {
            throw new Error('User Id is undefined');
          }

          window.localStorage.setItem(
            `CognitoIdentityServiceProvider.${portalConfig.AWS.Auth.userPoolWebClientId}.LastAuthUser`,
            userId,
          );
          const session = await Auth.currentSession();
          const accessToken = session.getAccessToken().getJwtToken();

          await UsersClient.setUserSession({
            ...defaultPortalSessionData,
            portalId,
            accessToken,
          });

          // set the next query param so that server-side can redirect to the correct page
          // if there is an authorized session for the portal
          window.location.replace(
            `/portal/${portalId}?next=${query.next || ''}`,
          );
          return;
        }
      }

      setAuthenticatedPortalsData({
        authSession: workspaceLoginSession,
        authenticatedPortals: workspacesResponse,
      });
      setLoadingWorkspaces(false);
    } catch (error) {
      setLoadingWorkspaces(false);
      console.error('Error while setting workspaces', error);
    }
  };

  const handleAuthComplete = () => {
    // save authenticated portal in local storage
    // so that we can list them in the workspace dropdown menu
    loadWorkspaces();
  };

  // execute the auth complete
  // this should be done when we were processing an oauth callback and
  // have succuessfullt signed in
  if (googleSignedIn && googleSignInProgress) {
    Auth.currentAuthenticatedUser().then(user => {
      const email = StringUtils.RemoveSpaceToLowerCase(user.attributes.email);
      UsersClient.setAppUserCookie(
        user,
        'workspace_login',
        'workspaces',
        email,
      ).then(() => {
        window.localStorage.setItem(
          `CognitoIdentityServiceProvider.${portalConfig.AWS.Auth.userPoolWebClientId}.LastAuthUser`,
          user.username,
        );

        dispatch(setProcessingCallback(false));
        handleAuthComplete();
      });
    });
  }

  return (
    <OnboardingPageLayout data-test-i="internal-login-page">
      <LoadingWrapper
        hideContentWhileLoading
        isLoading={googleSignInProgress || loadingWorkspaces}
      >
        {!authenticatedPortalsData ? (
          <OnboardingAuthLayout onAuthComplete={handleAuthComplete} />
        ) : (
          <OnboardingWorkspaceComponent
            onWorkspaceSelected={handleWorkspaceSelected}
            authenticatedPortals={authenticatedPortalsData.authenticatedPortals}
          />
        )}
      </LoadingWrapper>
    </OnboardingPageLayout>
  );
};
